import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { languagesApi, translationsApi } from "@/apis/apis";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import { Get } from "@/Axios/AxiosFunctions";
import RenderToast from "@/components/RenderToast";
import getTranslationInitialValues from "@/data/LanguageManagement/Translations/initial-values";
import getTranslationSchema from "@/schema/LanguageManagement/Translations/schema";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import { useSelector } from "react-redux";
import { DEFAULT_LANGUAGE, EN_US_CODE } from "@/constant/Languages/language";

const TranslationModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const showApi = modalState.id ? `${translationsApi}/${modalState.id}` : null;
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const { accessToken } = useSelector((state) => state?.authReducer);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [responseData, isSuccess] = await Get({
          route: `${languagesApi}`,
          accessToken,
        });
        if (isSuccess) {
          setAvailableLanguages(responseData?.data?.data);
        }
      } catch (error) {
        console.error("Error fetching translation data:", error);
      }
    };

    fetchData();
  }, []);

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Translation ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${translationsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: translationsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getTranslationInitialValues(modalState.id ? showData : null),
    validationSchema: getTranslationSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("key", values.key);

    availableLanguages.forEach((language) => {
      formData.append(`${language.slug}`, values[language.slug] || "");
    });

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Translation" : "Add Translation"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"key"}
            saveError={saveError}
            label="Key"
          />
        </Col>
      </Row>
      {availableLanguages.map((language) => (
        <Row key={language.id}>
          <Col md={12}>
            <BasicInput
              required={
                language.code === DEFAULT_LANGUAGE ||
                language.code === EN_US_CODE
              }
              formik={formik}
              fieldName={language.slug}
              saveError={saveError}
              label={`${language.title} (${language.code.toUpperCase()})`}
            />
          </Col>
        </Row>
      ))}
    </BasicModal>
  );
});

export default TranslationModal;
